import React from 'react';
import { useReactToPrint } from 'react-to-print';

const InvoiceModal = ({ subscription, onClose, userData }) => {
  const printRef = React.useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Invoice CFE-${Date.now().toString().slice(-6)}`,
    onAfterPrint: () => console.log('Invoice printed'),
  });

  const company = {
    name: 'Chicken Farm Experts',
    logo: '/logos/logo.png', // ← make sure this file exists in /public/logos/logo.png
    address: '51 Queen Ediths Way',
    city: 'Cambridge, United Kingdom',
    postalCode: 'CB1 8PJ',
    phone: '+44 161 566 9077',
    email: 'info@chickfarm.site',
  };

  const invoiceData = {
    invoiceNumber: `CFE-${Date.now().toString().slice(-6)}`,
    date: new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }),
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  };

  const customer = {
    name: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'Customer',
    email: userData?.email || 'N/A',
    phone: userData?.phone || 'N/A',
    address: [userData?.street, userData?.city, userData?.state, userData?.country, userData?.postalCode]
      .filter(Boolean)
      .join(', ') || 'N/A',
  };

  const formatCurrency = (amount) => `£${Number(amount || 0).toFixed(2)}`;

  const totalPrice = Number(subscription?.price || 0);
  const vatRate = 0.20;
  const amountExclVat = totalPrice / (1 + vatRate);
  const vatAmount = totalPrice - amountExclVat;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[96vh] overflow-y-auto mx-4 sm:mx-6">
        {/* Screen-only header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-xl font-bold">Invoice Preview</h2>
          <div className="flex gap-4">
            <button
              onClick={handlePrint}
              className="bg-white text-indigo-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Print / Save as PDF
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 px-4 py-2 transition"
            >
              Close
            </button>
          </div>
        </div>

        {/* This is what gets printed – exact same as modal */}
        <div ref={printRef} className="p-10 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-start gap-12 mb-12">
              <div>
                <img src={company.logo} alt={company.name} className="h-20 mb-6" />
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="font-bold text-2xl text-gray-900">{company.name}</div>
                  <div>{company.address}</div>
                  <div>{company.city}, {company.postalCode}</div>
                  <div>Phone: {company.phone}</div>
                  <div>Email: {company.email}</div>
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-3xl px-10 py-8 text-right w-80">
                <div className="text-5xl font-bold text-gray-900 mb-2">INVOICE</div>
                <div className="text-xl font-semibold text-gray-800">#{invoiceData.invoiceNumber}</div>
                <div className="text-gray-600 mt-4 space-y-1">
                  <div>Date: {invoiceData.date}</div>
                  <div className="font-semibold">Due: {invoiceData.dueDate}</div>
                </div>
              </div>
            </div>

            {/* Bill To + Subscription */}
            <div className="grid grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <h3 className="font-bold text-lg mb-4">Bill To</h3>
                <div className="font-bold text-xl text-gray-900 mb-4">{customer.name}</div>
                <div className="text-sm text-gray-700 space-y-2">
                  <div>Email: {customer.email}</div>
                  <div>Phone: {customer.phone}</div>
                  <div>Address: {customer.address}</div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <h3 className="font-bold text-lg mb-4">Subscription</h3>
                <div className="font-bold text-3xl text-gray-900 mb-2">{subscription.name}</div>
                <div className="text-gray-600">
                  Period: {new Date(subscription.startDate).toLocaleDateString('en-GB')} –{' '}
                  {new Date(subscription.endDate).toLocaleDateString('en-GB')}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-12">
              <h3 className="font-bold text-lg mb-4">Plan Description & Benefits</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-[15px] leading-relaxed text-gray-800">
                {subscription.description ? (
                  <div dangerouslySetInnerHTML={{ __html: subscription.description }} />
                ) : (
                  <p className="italic text-gray-500">No description provided.</p>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-12">
              <h3 className="font-bold text-lg mb-4">Invoice Items</h3>
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-5 font-semibold">Description</th>
                    <th className="text-center p-5 font-semibold w-24">Qty</th>
                    <th className="text-right p-5 font-semibold w-40">Unit Price</th>
                    <th className="text-right p-5 font-semibold w-40">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-5 font-medium">{subscription.name}</td>
                    <td className="p-5 text-center">1</td>
                    <td className="p-5 text-right font-medium">{formatCurrency(totalPrice)}</td>
                    <td className="p-5 text-right font-bold text-indigo-700">{formatCurrency(totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 max-w-md ml-auto">
              <div className="space-y-4 text-sm">
                <div className="flex justify-between"><span>Subtotal (excl. VAT)</span><span>{formatCurrency(amountExclVat)}</span></div>
                <div className="flex justify-between"><span>VAT (20%)</span><span>{formatCurrency(vatAmount)}</span></div>
                <div className="flex justify-between text-lg font-bold text-indigo-700 pt-4 border-t border-gray-300">
                  <span>Total (incl. VAT)</span><span>{formatCurrency(totalPrice)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-700">
                  <span>AMOUNT PAID</span><span>{formatCurrency(totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-16 text-center text-xs text-gray-500">
              Thank you for choosing Chicken Farm Experts!<br />
              Questions? <span className="font-medium">{company.email}</span><br />
              Generated on {new Date().toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;